/**
 * Surface 1.2 — Person profile. Every figure, label, route and unit the page
 * renders resolves through this module or `@/demo/data`; the component itself
 * carries no literal.
 *
 * WHAT IS AUTHORED HERE. The seed holds what is true of all 19 people (office,
 * role, skills as tags, load, free, rate, languages, joined). The *deep*
 * profile the deck draws — verified skill levels, phases delivered with a
 * close-out rating, the growth plan and the 8-week booking sheet — is authored
 * once, here, for the three people the resource story is told through:
 * Julien Roux (the best fit), Arta Berisha (the growth move) and Endrit
 * Krasniqi (the cheaper option) — the same three the allocation screen ranks.
 * Everyone else renders an honest minimal view; the page says so rather than
 * inventing levels per visit.
 *
 * CONSISTENCY THAT MUST NOT DRIFT. The Van Dijk migration task appears on all
 * three deep profiles as `Proposed by PX`, never as booked, and its hours come
 * from `OPEN_TASK.hours` — it is one open task with three ranked candidates,
 * and the allocation screen is where a human decides. Confirmed hours are
 * sized against each person's seeded `loadPct` so the profile and the capacity
 * view cannot disagree.
 */
import type { StatusTone } from '@/components/px/StatusPill'
import {
  OFFICES,
  OPEN_TASK,
  PEOPLE,
  PHASES,
  projectById,
  type OfficeCode,
  type Person,
  type PhaseKey,
} from '@/demo/data'

/* ── Routes — no surface builds an href by hand ─────────────────────────── */

export const POOL_HREF = '/resources/pool'
export const SKILLS_HREF = '/resources/skills'
export const TRAINING_HREF = '/resources/training'
export const ALLOCATIONS_HREF = '/resources/allocations'
export const PROJECTS_HREF = '/projects'

export const personHref = (personId: string): string => `/resources/people/${personId}`

/* ── Who this surface resolves to ───────────────────────────────────────── */

/** The hero profile — also the fallback for a missing or unknown route param. */
export const HERO_PERSON_ID = 'julien-roux'

/** The three people whose deep profile is authored this round. */
export const DEEP_PROFILE_IDS = ['julien-roux', 'arta-berisha', 'endrit-krasniqi'] as const

export const isPersonId = (id: string | undefined): id is string =>
  id !== undefined && PEOPLE.some((person) => person.id === id)

/** Route param → a person id that exists in the seed (hero when it does not). */
export const resolvePersonId = (param: string | undefined): string =>
  isPersonId(param) ? param : HERO_PERSON_ID

export const officeCity = (code: OfficeCode): string => {
  const office = OFFICES.find((o) => o.code === code)
  if (!office) throw new Error(`Unknown office: ${code}`)
  return office.city
}

export const phaseName = (key: PhaseKey): string => {
  const phase = PHASES.find((p) => p.key === key)
  if (!phase) throw new Error(`Unknown phase: ${key}`)
  return phase.name
}

/**
 * Header line: office · city · role · joined {year} · languages. Segments the
 * seed leaves empty (joined, languages are optional on Person) are dropped
 * rather than rendered as a gap.
 */
export const personMeta = (person: Person): string => {
  const { languages = [], joined } = person
  return [
    person.office,
    officeCity(person.office),
    person.role,
    joined ? `joined ${joined}` : null,
    languages.length > 0 ? languages.join('/') : null,
  ]
    .filter((segment): segment is string => segment !== null)
    .join(' · ')
}

/* ── Panel shapes ───────────────────────────────────────────────────────── */

export interface VerifiedSkill {
  label: string
  /** Verified level, 0–100 — delivered work rated at close-out + assessment. */
  pct: number
  /** What backs the level; never a self-declaration. */
  evidence: string
}

export interface PhaseRecord {
  phase: PhaseKey
  projects: number
  /** Close-out rating out of RATING_SCALE — never derived from timesheets. */
  rating: number
}

export const RATING_SCALE = 5

export type GrowthStatus = 'Booked · Oct' | 'Waitlist' | 'Suggested'

export interface GrowthItem {
  course: string
  status: GrowthStatus
  /** Why PX suggests it, phrased as pipeline demand against certified supply. */
  why: string
}

export type BookingStatus = 'Confirmed' | 'Proposed by PX' | 'Pencilled'

export interface Booking {
  /** Seeded project, or null when the commitment is not a client project. */
  projectId: string | null
  /** Used only when projectId is null (Competence Center work). */
  label?: string
  role: string
  weeks: string
  hours: number
  status: BookingStatus
  href: string
  /** One line of honesty attached to the row — e.g. a proposal is not a booking. */
  note?: string
}

export interface DeepProfile {
  skills: readonly VerifiedSkill[]
  phases: readonly PhaseRecord[]
  growth: readonly GrowthItem[]
  bookings: readonly Booking[]
}

/** Locked status → pill tone (StatusPill's canonical AA set). */
export const GROWTH_TONE: Record<GrowthStatus, StatusTone> = {
  'Booked · Oct': 'neutral',
  Waitlist: 'review',
  Suggested: 'proposed',
}

export const BOOKING_TONE: Record<BookingStatus, StatusTone> = {
  Confirmed: 'done',
  'Proposed by PX': 'proposed',
  Pencilled: 'neutral',
}

/* ── The 8-week horizon ─────────────────────────────────────────────────── */

/**
 * The deck's capacity heat row (screen 1.3) runs W34→W45 with W34 as the
 * current week; the booking sheet anchors on the same W34 so the two surfaces
 * describe one calendar.
 */
export const CURRENT_WEEK = 34
export const HORIZON_WEEKS = 8
export const HORIZON_LABEL = `W${CURRENT_WEEK}–W${CURRENT_WEEK + HORIZON_WEEKS - 1}`

const weeks = (from: number, to?: number): string =>
  to === undefined ? `W${from}` : `W${from}–W${to}`

/* ── The Van Dijk open task, as it appears on a profile ─────────────────── */

const VAN_DIJK_ID = 'van-dijk'

/**
 * The one open task, ranked across three candidates on the allocation screen.
 * Hours are read from the seed so the profile and the allocation table state
 * the same 32 h.
 */
const openTaskBooking = (role: string): Booking => ({
  projectId: VAN_DIJK_ID,
  role,
  weeks: weeks(CURRENT_WEEK + 2, CURRENT_WEEK + 4),
  hours: OPEN_TASK.hours,
  status: 'Proposed by PX',
  href: ALLOCATIONS_HREF,
  note: 'PX ranks three candidates for this task — no hours are booked until a manager signs off.',
})

/* ── Authored deep profiles ─────────────────────────────────────────────── */

const JULIEN: DeepProfile = {
  skills: [
    { label: 'Odoo data migration', pct: 96, evidence: '7 migrations delivered · Competence Center assessment' },
    { label: 'QA & regression', pct: 88, evidence: '5 test phases closed · certified' },
    { label: 'Retail data models', pct: 84, evidence: '4 retail rollouts · expert sign-off' },
    { label: 'Fit-gap analysis', pct: 72, evidence: '2 discovery phases · assisted, not yet certified' },
  ],
  phases: [
    { phase: 'migrate', projects: 7, rating: 4.8 },
    { phase: 'test', projects: 5, rating: 4.6 },
    { phase: 'build', projects: 2, rating: 4.2 },
  ],
  growth: [
    {
      course: 'Odoo 19 migration toolkit · advanced',
      status: 'Booked · Oct',
      why: 'Four Odoo 19 rollouts enter Migrate before Q1 and the toolkit badge expires with the version.',
    },
    {
      course: 'Payroll BE · certification',
      status: 'Waitlist',
      why: 'Pipeline demand for payroll BE exceeds certified supply from W44 — one certified consultant covers it today.',
    },
    {
      course: 'Migration cohort · teaching track',
      status: 'Suggested',
      why: 'The Kosovo migration cohort needs a certified lead; demand for migration engineers outruns certified supply by two from W41.',
    },
  ],
  bookings: [
    {
      projectId: 'lumiere',
      role: 'Migration lead',
      weeks: weeks(CURRENT_WEEK, CURRENT_WEEK + 4),
      hours: 120,
      status: 'Confirmed',
      href: PROJECTS_HREF,
    },
    openTaskBooking('Data migration — field mapping & load scripts'),
    {
      projectId: 'roosendaal',
      role: 'QA & regression',
      weeks: weeks(CURRENT_WEEK + 6, CURRENT_WEEK + 7),
      hours: 24,
      status: 'Pencilled',
      href: PROJECTS_HREF,
    },
  ],
}

const ARTA: DeepProfile = {
  skills: [
    { label: 'Odoo development', pct: 78, evidence: '3 build phases delivered · certified' },
    { label: 'Data migration · assisted', pct: 54, evidence: '1 migration paired with a certified engineer' },
    { label: 'Retail data models', pct: 46, evidence: 'Competence Center assessment · no delivered work yet' },
    { label: 'Regression testing', pct: 38, evidence: 'cohort 4 coursework · observing on Van Dijk' },
  ],
  phases: [
    { phase: 'build', projects: 3, rating: 4.5 },
    { phase: 'migrate', projects: 1, rating: 4.1 },
  ],
  growth: [
    {
      course: 'Certified migration engineer · cohort 5',
      status: 'Booked · Oct',
      why: 'One certified migration engineer covers four Odoo 19 rollouts entering Migrate — certified supply is the constraint, not headcount.',
    },
    {
      course: 'Odoo 19 developer certification',
      status: 'Waitlist',
      why: 'The version badge expires with Odoo 18; every developer on a 19 rollout needs it before the November cutovers.',
    },
    {
      course: 'Retail data models',
      status: 'Suggested',
      why: 'Retail is the busiest vertical in the pipeline and Kosovo holds no certified retail data modeller.',
    },
  ],
  bookings: [
    {
      projectId: VAN_DIJK_ID,
      role: 'Build support · pricing & promotions',
      weeks: weeks(CURRENT_WEEK, CURRENT_WEEK + 4),
      hours: 88,
      status: 'Confirmed',
      href: PROJECTS_HREF,
    },
    openTaskBooking('Data migration — paired with a certified engineer'),
    {
      projectId: null,
      label: 'Competence Center · cohort 5',
      role: 'Learner · migration track',
      weeks: weeks(CURRENT_WEEK + 5, CURRENT_WEEK + 7),
      hours: 24,
      status: 'Pencilled',
      href: TRAINING_HREF,
    },
  ],
}

const ENDRIT: DeepProfile = {
  skills: [
    { label: 'Fit-gap analysis', pct: 82, evidence: '4 discovery phases closed · certified' },
    { label: 'Delivery coordination', pct: 76, evidence: '3 design phases · expert sign-off' },
    { label: 'Logistics data models', pct: 58, evidence: 'Vermeer rollout · assisted' },
    { label: 'Odoo data migration', pct: 44, evidence: 'Competence Center assessment · one supervised load' },
  ],
  phases: [
    { phase: 'design', projects: 3, rating: 4.6 },
    { phase: 'discover', projects: 4, rating: 4.4 },
    { phase: 'test', projects: 2, rating: 4.2 },
  ],
  growth: [
    {
      course: 'Solution architect track · module 2',
      status: 'Booked · Oct',
      why: 'Blueprint sign-off sits with one architect for every rollout; the pipeline adds three before Q1.',
    },
    {
      course: 'Project management essentials',
      status: 'Waitlist',
      why: 'Two delivery leads run at over 90% load — the group needs a third before the November go-lives.',
    },
    {
      course: 'Logistics vertical certification',
      status: 'Suggested',
      why: 'Logistics deals in the pipeline outrun certified supply from W41; the vertical rests on one certified consultant.',
    },
  ],
  bookings: [
    {
      projectId: 'vermeer',
      role: 'UAT coordination',
      weeks: weeks(CURRENT_WEEK, CURRENT_WEEK + 6),
      hours: 224,
      status: 'Confirmed',
      href: PROJECTS_HREF,
    },
    openTaskBooking('Data migration — field mapping & load scripts'),
    {
      projectId: 'brugge-bouw',
      role: 'Fit-gap analysis',
      weeks: weeks(CURRENT_WEEK + 7),
      hours: 16,
      status: 'Pencilled',
      href: PROJECTS_HREF,
    },
  ],
}

const DEEP_PROFILES: Readonly<Record<string, DeepProfile>> = {
  'julien-roux': JULIEN,
  'arta-berisha': ARTA,
  'endrit-krasniqi': ENDRIT,
}

/** The authored profile for a person, or null when only the seed is known. */
export const deepProfileFor = (personId: string): DeepProfile | null =>
  DEEP_PROFILES[personId] ?? null

/* ── Minimal view — the other 16 people ─────────────────────────────────── */

export interface MinimalBooking {
  label: string
  href: string
  role: string
}

/** What the seed alone supports: declared skills and the current engagement. */
export const minimalBooking = (person: Person): MinimalBooking | null => {
  const { currentProject = null } = person
  if (currentProject === null) return null
  return {
    label: projectById(currentProject).client,
    href: PROJECTS_HREF,
    role: person.role,
  }
}

/* ── Derived figures ────────────────────────────────────────────────────── */

export interface BookingTotals {
  confirmed: number
  proposed: number
  pencilled: number
}

export const bookingTotals = (bookings: readonly Booking[]): BookingTotals =>
  bookings.reduce<BookingTotals>(
    (totals, booking) => ({
      confirmed: totals.confirmed + (booking.status === 'Confirmed' ? booking.hours : 0),
      proposed: totals.proposed + (booking.status === 'Proposed by PX' ? booking.hours : 0),
      pencilled: totals.pencilled + (booking.status === 'Pencilled' ? booking.hours : 0),
    }),
    { confirmed: 0, proposed: 0, pencilled: 0 }
  )

export const bookingLabel = (booking: Booking): string => {
  if (booking.projectId !== null) return projectById(booking.projectId).client
  if (booking.label !== undefined) return booking.label
  throw new Error('Booking has neither a seeded project nor a label')
}

export const projectsDelivered = (phases: readonly PhaseRecord[]): number =>
  phases.reduce((total, record) => total + record.projects, 0)

/* ── Chrome + copy ──────────────────────────────────────────────────────── */

export const personChrome = (person: Person) =>
  ({
    moduleLabel: 'PX · Resources',
    contextPill: 'PROFILE',
    breadcrumbPeople: 'People',
    breadcrumbName: person.name,
    backLabel: '← Resource pool',
  }) as const

export const PERSON_PROFILE_COPY = {
  freePill: (freePct: number) => `${freePct}% free`,
  ratePill: (rate: number) => `€${rate}/h`,
  skills: {
    label: 'VERIFIED SKILLS',
    note: 'Verified by delivered work + Competence Center assessment — never self-declared.',
    listLabel: (name: string) => `Verified skills for ${name}`,
    linkHint: 'where the group is thin',
  },
  phases: {
    label: 'PHASES DELIVERED',
    note: 'Rating from project close-out, not timesheets.',
    columns: { phase: 'Phase', projects: 'Projects', rating: `Rating (of ${RATING_SCALE})` },
    caption: (name: string) =>
      `Lifecycle phases ${name} has delivered, with the number of projects and the average close-out rating.`,
    ratingLabel: (rating: number) => rating.toFixed(1),
    totalLine: (projects: number, phaseCount: number) =>
      `${projects} projects across ${phaseCount} phases`,
  },
  growth: {
    label: 'GROWTH PLAN · COMPETENCE CENTER',
    note: 'Each course is triggered by the skills matrix — pipeline demand measured against certified supply.',
    whyLabel: 'Why PX suggests it',
  },
  bookings: {
    label: `BOOKED AND PLANNED · NEXT ${HORIZON_WEEKS} WEEKS`,
    note: `${HORIZON_LABEL} · confirmed hours are booked, proposed hours are not.`,
    columns: {
      project: 'Project',
      role: 'Role',
      weeks: 'Weeks',
      hours: 'Hours',
      status: 'Status',
    },
    caption: (name: string) =>
      `Work booked, proposed and pencilled for ${name} over the next ${HORIZON_WEEKS} weeks.`,
    hoursLabel: (hours: number) => `${hours} h`,
    totalsLine: (totals: BookingTotals) =>
      `${totals.confirmed} h confirmed · ${totals.proposed} h proposed by PX · ${totals.pencilled} h pencilled`,
  },
  minimal: {
    skillsLabel: 'DECLARED SKILLS',
    skillsNote:
      'Tags from the resource pool, not verified levels — no delivered work or assessment is attached to them yet.',
    bookingLabel: 'CURRENT ENGAGEMENT',
    benchLabel: '— on bench',
    withheldLead:
      'The deep profile — verified skill levels, phases delivered, the growth plan and the 8-week booking sheet — is authored this round for three people:',
    withheldTail:
      'The header above renders from the seed for all 19, so every profile route resolves.',
  },
  densityNote: {
    deep: (name: string) =>
      `This profile shows what the matching engine reads about ${name}: verified skill levels, the phases behind them, the growth plan and the next ${HORIZON_WEEKS} weeks. It withholds the full project history, timesheets and pay data — the hours here are capacity, never time recorded. Skill percentages come from work delivered and rated at project close-out plus a Competence Center assessment; the ${OPEN_TASK.hours} h Van Dijk task is a proposal on the allocation screen and becomes booked hours only after a manager signs off.`,
    minimal: (name: string) =>
      `Everything shown for ${name} comes from the resource pool record. Verified skill levels, phases delivered, the growth plan and the ${HORIZON_WEEKS}-week booking sheet are withheld rather than invented — three profiles carry them this round so the round is judged on real depth, not on filler.`,
  },
} as const
